/**
 * FoodPulse — canonical Product Taxonomy.
 *
 * Three-tier hierarchy:
 *
 *     Product Group  ─▶  Product Type  ─▶  Product Subtype  (─▶ Item)
 *
 *   Products
 *   ├── Food
 *   │   ├── Perishable           (fresh produce, meat, seafood, dairy, eggs, herbs…)
 *   │   ├── Non-Perishable       (grains, pasta, canned, dried, oils, sweeteners…)
 *   │   ├── Processed
 *   │   ├── Ultra-Processed
 *   │   ├── Beverages            (Alcoholic and Non-Alcoholic)
 *   │   ├── Ingredients & Additives
 *   │   ├── Baby Food
 *   │   └── Medical Nutrition
 *   │
 *   ├── Non-Food Grocery
 *   │   ├── Household Cleaning
 *   │   ├── Laundry
 *   │   ├── Personal Care
 *   │   ├── Baby Care
 *   │   ├── Health & Wellness
 *   │   ├── Paper Products
 *   │   ├── Food Storage
 *   │   ├── Kitchen Supplies
 *   │   ├── Pet Care
 *   │   ├── Pest Control
 *   │   ├── Home Essentials
 *   │   ├── Cosmetics
 *   │   ├── Fragrances
 *   │   ├── Stationery
 *   │   ├── Seasonal Products
 *   │   └── Automotive Essentials
 *   │
 *   └── Services  (Future)
 *       ├── Recipes
 *       ├── Nutrition Reports
 *       ├── Product Comparisons
 *       ├── Shopping Lists
 *       ├── Product Recalls
 *       ├── Sustainability Scores
 *       └── AI Health Advisor
 *
 * Every node carries a stable `slug` (used in URLs, DB fields and the
 * Python migration at backend/scripts/migrate_product_taxonomy.py —
 * keep the two files in sync).
 */

// -- Utility -------------------------------------------------------
const s = (label, slug, children = []) => ({ label, slug, children });


// ─── FOOD ─── Perishable ─────────────────────────────────────────
const FOOD_PERISHABLE_TYPES = [
  s("Fresh Fruits",       "fresh-fruits", [
    s("Pome",       "pome"),        s("Stone",      "stone"),
    s("Berries",    "berries"),     s("Citrus",     "citrus"),
    s("Tropical",   "tropical"),    s("Exotic",     "exotic"),
    s("Melons",     "melons"),      s("Grapes",     "grapes"),
    s("Dried (Fresh Style)", "dried-fresh"),
  ]),
  s("Fresh Vegetables",   "fresh-vegetables", [
    s("Leafy Greens",     "leafy-greens"),
    s("Root Vegetables",  "root-vegetables"),
    s("Tubers",           "tubers"),
    s("Mushrooms",        "mushrooms"),
    s("Sea Vegetables",   "sea-vegetables"),
    s("Sprouts",          "sprouts"),
    s("Edible Flowers",   "edible-flowers"),
    s("Peas & Beans (Fresh)", "peas-fresh"),
    s("Other Vegetables", "vegetables"),
  ]),
  s("Fresh Herbs",        "fresh-herbs"),
  s("Meat",               "meat", [
    s("Beef",    "beef"),   s("Pork",    "pork"),
    s("Lamb",    "lamb"),   s("Goat",    "goat"),
    s("Game",    "game-meat"), s("Offal", "offal"),
    s("Charcuterie", "charcuterie"),
  ]),
  s("Poultry",            "poultry", [
    s("Chicken", "chicken"), s("Duck", "duck"),
    s("Turkey",  "turkey"),  s("Game Birds", "game-bird"),
  ]),
  s("Fish",               "fish", [
    s("White Fish", "white-fish"),
    s("Oily Fish",  "oily-fish"),
    s("Salmon",     "salmon"),
    s("Sardines",   "sardines"),
    s("Freshwater", "freshwater"),
  ]),
  s("Seafood & Shellfish", "seafood-shellfish", [
    s("Shrimp",    "shrimp"),  s("Crab",    "crab"),
    s("Lobster",   "lobster"), s("Squid",   "squid"),
    s("Prepared Seafood", "prepared-seafood"),
  ]),
  s("Dairy",              "dairy", [
    s("Milk",        "milk"),         s("Cheese",   "cheese"),
    s("Yoghurt",     "yoghurt"),      s("Butter",   "butter"),
    s("Cream",       "cream"),        s("Ghee",     "ghee"),
  ]),
  s("Eggs",               "eggs"),
  s("Fermented (Fresh)",  "fermented-fresh"),
];


// ─── FOOD ─── Non-Perishable ─────────────────────────────────────
const FOOD_NON_PERISHABLE_TYPES = [
  s("Grains & Cereals", "grains-cereals", [
    s("Whole Grains", "whole-grains"),
    s("Processed Grains", "processed-grains"),
    s("Breakfast Cereals", "breakfast-cereals"),
  ]),
  s("Pasta & Noodles", "pasta-noodles", [
    s("Dry Pasta", "dry-pasta"),
    s("Egg Noodles", "egg-noodles"),
    s("Rice Noodles", "rice-noodles"),
    s("Instant Noodles", "instant-noodles"),
  ]),
  s("Legumes & Pulses", "legumes-pulses"),
  s("Nuts & Seeds", "nuts-seeds", [
    s("Nuts", "nuts"),
    s("Seeds", "seeds"),
  ]),
  s("Baking Ingredients", "baking-ingredients"),
  s("Sugars & Sweeteners", "sugars-sweeteners"),
  s("Oils & Fats", "oils-fats"),
  s("Herbs & Spices (Dried)", "herbs-spices-dried", [
    s("Dried Herbs",  "dried-herbs"),
    s("Whole Spices", "whole-spices"),
    s("Ground Spices","ground-spices"),
  ]),
  s("Condiments & Sauces", "condiments-sauces"),
  s("Canned Foods", "canned-foods", [
    s("Canned Vegetables", "canned-vegetables"),
    s("Canned Fruits",     "canned-fruits"),
    s("Canned Meat",       "canned-meat"),
    s("Canned Seafood",    "canned-seafood"),
    s("Canned Soups",      "canned-soups"),
    s("Canned Beans",      "canned-beans"),
  ]),
  s("Jarred Foods", "jarred-foods"),
  s("Dry Snacks", "dry-snacks"),
  s("Chips & Savoury Snacks", "chips-savoury-snacks"),
  s("Candy & Confectionery", "candy-confectionery"),
  s("Dried Fruits", "dried-fruits"),
  s("Dehydrated Foods", "dehydrated-foods"),
  s("Instant Foods", "instant-foods"),
  s("Beverage Products (Shelf-Stable)", "shelf-stable-beverages", [
    s("Coffee", "coffee"),
    s("Tea",    "tea"),
    s("Drink Mixes", "drink-mixes"),
  ]),
  s("Shelf-Stable Baby Foods", "shelf-stable-baby-foods"),
  s("Medical & Nutritional Foods", "medical-nutritional-foods"),
  s("Plant-Based Shelf-Stable Foods", "plant-based-shelf-stable"),
  s("International Foods", "international-foods"),
  s("Emergency & Survival Foods", "emergency-foods"),
  s("Pet Food",  "pet-food"),
];


// ─── FOOD ─── Processed / Ultra / Beverages / Ingredients / Baby / Medical ─
const FOOD_PROCESSED_TYPES = [
  s("Ready-to-Eat Meals",       "ready-to-eat"),
  s("Deli & Prepared Foods",    "deli-prepared"),
  s("Frozen Prepared Foods",    "frozen-prepared"),
  s("Baked Goods",              "baked-goods"),
  s("Cheese-based Products",    "cheese-products"),
  s("Cured Meats",              "cured-meats"),
];

const FOOD_ULTRA_PROCESSED_TYPES = [
  s("Sweetened Beverages",      "sweetened-beverages"),
  s("Instant Meals",            "ultra-instant-meals"),
  s("Fast-Food Style Products", "fast-food-style"),
  s("Highly Processed Snacks",  "highly-processed-snacks"),
];

const FOOD_BEVERAGES_TYPES = [
  s("Non-Alcoholic Beverages", "non-alcoholic-beverages", [
    s("Water",         "water"),
    s("Juice",         "juice"),
    s("Milk & Dairy",  "beverage-dairy"),
    s("Plant Milks",   "plant-milks"),
    s("Soft Drinks",   "soft-drinks"),
    s("Energy Drinks", "energy-drinks"),
    s("Sports Drinks", "sports-drinks"),
    s("Tea & Coffee",  "tea-coffee-drinks"),
    s("Kombucha & Fermented Drinks", "kombucha"),
  ]),
  s("Alcoholic Beverages", "alcoholic-beverages", [
    s("Beer",   "beer"),
    s("Wine",   "wine"),
    s("Spirits","spirits"),
    s("Cider",  "cider"),
    s("Sake",   "sake"),
  ]),
];

const FOOD_INGREDIENTS_TYPES = [
  s("Flavourings & Extracts",       "flavourings"),
  s("Colourings",                   "colourings"),
  s("Preservatives",                "preservatives"),
  s("Thickeners & Stabilisers",     "thickeners"),
  s("Sweeteners (Ingredient Form)", "ingredient-sweeteners"),
  s("Yeasts & Starters",            "starters"),
];

const FOOD_BABY_TYPES = [
  s("Infant Formula",       "infant-formula"),
  s("Baby Cereals",         "baby-cereals"),
  s("Purees & Jars",        "purees"),
  s("Toddler Snacks",       "toddler-snacks"),
];

const FOOD_MEDICAL_TYPES = [
  s("Meal Replacement Powders",  "meal-replacement"),
  s("Protein Powders",           "protein-powders"),
  s("Oral Nutrition Drinks",     "oral-nutrition-drinks"),
  s("High-Calorie Supplements",  "high-calorie-supplements"),
];


// ─── NON-FOOD GROCERY ────────────────────────────────────────────
const NONFOOD_HOUSEHOLD = [
  s("All-Purpose Cleaners", "all-purpose-cleaner"),
  s("Glass Cleaners",       "glass-cleaner"),
  s("Floor Cleaners",       "floor-cleaner"),
  s("Bathroom Cleaners",    "bathroom-cleaner"),
  s("Toilet Cleaners",      "toilet-cleaner"),
  s("Kitchen Cleaners",     "kitchen-cleaner"),
  s("Oven Cleaners",        "oven-cleaner"),
  s("Disinfectants",        "disinfectant"),
  s("Bleach",               "bleach"),
  s("Mould & Mildew Removers", "mould-mildew"),
  s("Drain Cleaners",       "drain-cleaner"),
  s("Furniture Polish",     "furniture-polish"),
  s("Carpet Cleaners",      "carpet-cleaner"),
  s("Air Fresheners",       "air-freshener"),
];
const NONFOOD_LAUNDRY = [
  s("Laundry Detergent",    "laundry-detergent"),
  s("Fabric Softener",      "fabric-softener"),
  s("Stain Removers",       "stain-remover"),
  s("Laundry Bleach",       "laundry-bleach"),
  s("Dryer Sheets",         "dryer-sheets"),
  s("Laundry Pods",         "laundry-pods"),
  s("Laundry Sanitisers",   "laundry-sanitiser"),
];
const NONFOOD_DISH = [
  s("Dishwashing Liquid",   "dishwash-liquid"),
  s("Dishwasher Tablets",   "dishwasher-tablets"),
  s("Dishwasher Powder",    "dishwasher-powder"),
  s("Rinse Aid",            "rinse-aid"),
  s("Dish Sponges",         "dish-sponges"),
  s("Scrubbers",            "scrubbers"),
  s("Steel Wool",           "steel-wool"),
];
const NONFOOD_PAPER = [
  s("Toilet Paper",         "toilet-paper"),
  s("Facial Tissue",        "facial-tissue"),
  s("Paper Towels",         "paper-towels"),
  s("Napkins",              "napkins"),
  s("Paper Plates",         "paper-plates"),
  s("Paper Cups",           "paper-cups"),
  s("Paper Bowls",          "paper-bowls"),
];
const NONFOOD_FOOD_STORAGE = [
  s("Aluminium Foil",       "aluminium-foil"),
  s("Plastic Wrap",         "plastic-wrap"),
  s("Parchment Paper",      "parchment-paper"),
  s("Wax Paper",            "wax-paper"),
  s("Freezer Bags",         "freezer-bags"),
  s("Storage Bags",         "storage-bags"),
  s("Food Containers",      "food-containers"),
  s("Vacuum Storage Bags",  "vacuum-storage-bags"),
];
const NONFOOD_TRASH = [
  s("Garbage Bags",         "garbage-bags"),
  s("Compost Bags",         "compost-bags"),
  s("Recycling Bags",       "recycling-bags"),
  s("Trash Cans",           "trash-cans"),
  s("Bin Liners",           "bin-liners"),
];
const NONFOOD_PERSONAL_CARE = [
  s("Oral Care",            "oral-care", [
    s("Toothpaste", "toothpaste"), s("Toothbrushes", "toothbrushes"),
    s("Mouthwash",  "mouthwash"),  s("Dental Floss", "dental-floss"),
  ]),
  s("Bath & Body",          "bath-body", [
    s("Soap",       "soap"),        s("Body Wash",    "body-wash"),
    s("Shampoo",    "shampoo"),     s("Conditioner",  "conditioner"),
    s("Lotion",     "lotion"),      s("Moisturiser",  "moisturiser"),
    s("Hand Soap",  "hand-soap"),
  ]),
  s("Hair Care",            "hair-care", [
    s("Hair Gel",   "hair-gel"),    s("Hair Spray",   "hair-spray"),
    s("Hair Colour","hair-colour"), s("Hair Oil",     "hair-oil"),
  ]),
  s("Shaving",              "shaving", [
    s("Razors",         "razors"),
    s("Shaving Cream",  "shaving-cream"),
    s("Aftershave",     "aftershave"),
  ]),
  s("Feminine Care",        "feminine-care", [
    s("Sanitary Pads",  "sanitary-pads"),
    s("Tampons",        "tampons"),
    s("Menstrual Cups", "menstrual-cups"),
    s("Panty Liners",   "panty-liners"),
  ]),
  s("Adult Care",           "adult-care", [
    s("Adult Diapers", "adult-diapers"),
    s("Incontinence Products", "incontinence"),
  ]),
];
const NONFOOD_BABY = [
  s("Diapers",              "diapers"),
  s("Baby Wipes",           "baby-wipes"),
  s("Baby Lotion",          "baby-lotion"),
  s("Baby Shampoo",         "baby-shampoo"),
  s("Baby Powder",          "baby-powder"),
  s("Nursing Pads",         "nursing-pads"),
  s("Bottle Cleaners",      "bottle-cleaners"),
];
const NONFOOD_HEALTH = [
  s("First Aid Kits",       "first-aid-kits"),
  s("Bandages",             "bandages"),
  s("Cotton Balls",         "cotton-balls"),
  s("Cotton Swabs",         "cotton-swabs"),
  s("Antiseptics",          "antiseptics"),
  s("Thermometers",         "thermometers"),
  s("Face Masks",           "face-masks"),
  s("Hand Sanitisers",      "hand-sanitisers"),
];
const NONFOOD_VITAMINS = [
  s("Multivitamins",        "multivitamins"),
  s("Vitamin C",            "vitamin-c"),
  s("Vitamin D",            "vitamin-d"),
  s("Fish Oil",             "fish-oil"),
  s("Probiotics",           "probiotics-supp"),
  s("Protein Supplements",  "protein-supplements"),
  s("Herbal Supplements",   "herbal-supplements"),
];
const NONFOOD_COSMETICS = [
  s("Foundation",           "foundation"),
  s("Lipstick",             "lipstick"),
  s("Mascara",              "mascara"),
  s("Eyeliner",             "eyeliner"),
  s("Powder",               "powder"),
  s("Blush",                "blush"),
  s("Makeup Remover",       "makeup-remover"),
  s("Nail Polish",          "nail-polish"),
];
const NONFOOD_FRAGRANCE = [
  s("Perfume",              "perfume"),
  s("Cologne",              "cologne"),
  s("Body Spray",           "body-spray"),
  s("Deodorant",            "deodorant"),
];
const NONFOOD_PET = [
  s("Pet Shampoo",          "pet-shampoo"),
  s("Pet Toys",             "pet-toys"),
  s("Cat Litter",           "cat-litter"),
  s("Pet Treats",           "pet-treats"),
  s("Grooming Supplies",    "grooming-supplies"),
  s("Pet Hygiene Products", "pet-hygiene"),
];
const NONFOOD_PEST = [
  s("Insect Spray",         "insect-spray"),
  s("Ant Bait",             "ant-bait"),
  s("Rat Traps",            "rat-traps"),
  s("Mosquito Repellent",   "mosquito-repellent"),
  s("Fly Traps",            "fly-traps"),
];
const NONFOOD_HOME_ESSENTIALS = [
  s("Candles",              "candles"),
  s("Matches",              "matches"),
  s("Lighters",             "lighters"),
  s("Batteries",            "batteries"),
  s("Light Bulbs",          "light-bulbs"),
  s("Extension Cords",      "extension-cords"),
];
const NONFOOD_KITCHEN = [
  s("Disposable Gloves",    "disposable-gloves"),
  s("Cleaning Cloths",      "cleaning-cloths"),
  s("Kitchen Towels",       "kitchen-towels"),
  s("Measuring Cups",       "measuring-cups"),
  s("Food Thermometers",    "food-thermometers"),
  s("Oven Mitts",           "oven-mitts"),
];
const NONFOOD_STATIONERY = [
  s("Pens",                 "pens"),
  s("Pencils",              "pencils"),
  s("Markers",              "markers"),
  s("Notebooks",            "notebooks"),
  s("Sticky Notes",         "sticky-notes"),
  s("Printer Paper",        "printer-paper"),
];
const NONFOOD_SEASONAL = [
  s("Holiday Decorations",  "holiday-decorations"),
  s("Gift Wrap",            "gift-wrap"),
  s("Picnic Supplies",      "picnic-supplies"),
  s("Disposable Cutlery",   "disposable-cutlery"),
  s("Charcoal",             "charcoal"),
  s("Fire Starters",        "fire-starters"),
];
const NONFOOD_AUTOMOTIVE = [
  s("Windshield Washer Fluid", "windshield-washer"),
  s("Motor Oil",               "motor-oil"),
  s("Car Cleaning Products",   "car-cleaning"),
  s("Ice Scrapers",            "ice-scrapers"),
];
const NONFOOD_MISC = [
  s("Zip Ties",       "zip-ties"),
  s("Adhesives",      "adhesives"),
  s("Tape",           "tape"),
  s("Glue",           "glue"),
  s("Rope",           "rope"),
  s("Utility Knives", "utility-knives"),
  s("Flashlights",    "flashlights"),
];


// ─── SERVICES (future placeholder) ───────────────────────────────
const SERVICES_TYPES = [
  s("Recipes",              "svc-recipes"),
  s("Nutrition Reports",    "svc-nutrition-reports"),
  s("Product Comparisons",  "svc-product-comparisons"),
  s("Shopping Lists",       "svc-shopping-lists"),
  s("Product Recalls",      "svc-product-recalls"),
  s("Sustainability Scores","svc-sustainability-scores"),
  s("AI Health Advisor",    "svc-ai-health-advisor"),
];


// ─── Top-level tree ──────────────────────────────────────────────
export const PRODUCT_TAXONOMY = [
  s("Food", "food", [
    s("Perishable Foods",       "perishable",       FOOD_PERISHABLE_TYPES),
    s("Non-Perishable Foods",   "non-perishable",   FOOD_NON_PERISHABLE_TYPES),
    s("Processed Foods",        "processed",        FOOD_PROCESSED_TYPES),
    s("Ultra-Processed Foods",  "ultra-processed",  FOOD_ULTRA_PROCESSED_TYPES),
    s("Beverages",              "beverages-group",  FOOD_BEVERAGES_TYPES),
    s("Ingredients & Additives","ingredients",      FOOD_INGREDIENTS_TYPES),
    s("Baby Food",              "baby-food",        FOOD_BABY_TYPES),
    s("Medical Nutrition",      "medical-nutrition",FOOD_MEDICAL_TYPES),
  ]),
  s("Non-Food Grocery", "non-food", [
    s("Household Cleaning",     "household-cleaning", NONFOOD_HOUSEHOLD),
    s("Laundry Care",           "laundry",            NONFOOD_LAUNDRY),
    s("Dishwashing",            "dishwashing",        NONFOOD_DISH),
    s("Paper Products",         "paper-products",     NONFOOD_PAPER),
    s("Food Storage",           "food-storage-supplies", NONFOOD_FOOD_STORAGE),
    s("Trash & Recycling",      "trash-recycling",    NONFOOD_TRASH),
    s("Personal Care",          "personal-care",      NONFOOD_PERSONAL_CARE),
    s("Baby Care",              "baby-care",          NONFOOD_BABY),
    s("Health & Wellness",      "health-wellness",    NONFOOD_HEALTH),
    s("Vitamins & Supplements", "vitamins-supplements", NONFOOD_VITAMINS),
    s("Cosmetics",              "cosmetics",          NONFOOD_COSMETICS),
    s("Fragrances",             "fragrances",         NONFOOD_FRAGRANCE),
    s("Pet Care",               "pet-care",           NONFOOD_PET),
    s("Pest Control",           "pest-control",       NONFOOD_PEST),
    s("Home Essentials",        "home-essentials",    NONFOOD_HOME_ESSENTIALS),
    s("Kitchen Supplies",       "kitchen-supplies",   NONFOOD_KITCHEN),
    s("Stationery",             "stationery",         NONFOOD_STATIONERY),
    s("Seasonal Products",      "seasonal-products",  NONFOOD_SEASONAL),
    s("Automotive Essentials",  "automotive",         NONFOOD_AUTOMOTIVE),
    s("Miscellaneous",          "misc",               NONFOOD_MISC),
  ]),
  s("Services (Future)", "services", SERVICES_TYPES),
];


// -- Helpers -----------------------------------------------------

/**
 * Depth-first traversal — yields `{ path: [group,type,subtype?], node }`
 * for every leaf and interior node.
 */
export function walkTaxonomy(nodes = PRODUCT_TAXONOMY, path = []) {
  const out = [];
  for (const n of nodes) {
    const p = [...path, n];
    out.push({ path: p, node: n });
    if (n.children?.length) out.push(...walkTaxonomy(n.children, p));
  }
  return out;
}

export function findNode(slug, nodes = PRODUCT_TAXONOMY) {
  for (const n of nodes) {
    if (n.slug === slug) return n;
    if (n.children?.length) {
      const hit = findNode(slug, n.children);
      if (hit) return hit;
    }
  }
  return null;
}
